import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Monitor,
  AlertTriangle,
  BarChart3,
  Brain,
  Settings,
  TrendingUp,
  Menu,
  X,
  LogOut,
  Shield,
  Users,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { fetchAlerts } from '../lib/api';
import TenantSelector from './TenantSelector';
import LanguageToggle from './LanguageToggle';
import DarkModeToggle from './DarkModeToggle';
import AutoRefreshToggle from './AutoRefreshToggle';
import { cn } from '../lib/utils';

const navKeys = [
  { path: '/', key: 'nav.dashboard', icon: LayoutDashboard },
  { path: '/hosts', key: 'nav.hosts', icon: Monitor },
  { path: '/alerts', key: 'nav.alerts', icon: AlertTriangle },
  { path: '/analysis', key: 'nav.analysis', icon: BarChart3 },
  { path: '/ai-results', key: 'nav.aiReports', icon: Brain },
  { path: '/config', key: 'nav.config', icon: Settings },
  { path: '/trending', key: 'nav.trending', icon: TrendingUp },
  { path: '/clients', key: 'nav.clients', icon: Users },
  { path: '/remediations', key: 'nav.remediations', icon: Wrench },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [criticalCount, setCriticalCount] = useState(0);
  const location = useLocation();
  const { t } = useTranslation();
  const { userProfile, logout } = useAuth();
  const { tenants, selectedTenantId, setSelectedTenantId } = useTenant();
  const alertPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const pollAlerts = useCallback(() => {
    fetchAlerts({ hours: 24, severity: 'critical' })
      .then((alerts) => setCriticalCount(Array.isArray(alerts) ? alerts.length : 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    pollAlerts();
    alertPollRef.current = setInterval(pollAlerts, 30000);
    return () => { if (alertPollRef.current) clearInterval(alertPollRef.current); };
  }, [pollAlerts]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-brand-900 dark:bg-gray-950 text-white transform transition-transform lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
          <Shield className="w-8 h-8 text-blue-400" />
          <div>
            <h1 className="text-lg font-bold">SecuGuard</h1>
            <p className="text-xs text-blue-300">{t('ui.securityDashboard')}</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="mt-4 px-3 space-y-1">
          {navKeys.map((item) => {
            const Icon = item.icon;
            const isActive =
              item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-white/15 text-white'
                    : 'text-blue-200 hover:bg-white/10 hover:text-white'
                )}
              >
                <Icon className="w-5 h-5" />
                {t(item.key)}
                {item.path === '/alerts' && criticalCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {criticalCount > 99 ? '99+' : criticalCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold">
              {userProfile?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{userProfile?.name || 'User'}</p>
              <p className="text-xs text-blue-300 truncate">{userProfile?.email || ''}</p>
            </div>
            <button
              onClick={logout}
              className="text-blue-300 hover:text-white transition-colors"
              title={t('common.logout')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-4 lg:px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {t(navKeys.find(
              (n) =>
                n.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(n.path)
            )?.key || 'nav.dashboard')}
          </h2>
          <div className="ml-auto flex items-center gap-3">
            <AutoRefreshToggle />
            <DarkModeToggle />
            <LanguageToggle />
            <TenantSelector
              tenants={tenants}
              value={selectedTenantId}
              onChange={setSelectedTenantId}
            />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
